import {put, call} from 'redux-saga/effects';
import ImportExport from "../../entities/importExport";
import * as Actions from "../actions/importExportActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ImportExportAction = {
    importExportId: string;
    importExport: ImportExport;
    importExports: ImportExport[];
};

export function* fetchOneImportExport(action: ImportExportAction) {
	try {
		const importExport: ImportExport = yield call(api.findOne, 'importExport', action.importExportId);

		// Now we can update the user interface
		yield put({type: Actions.IMPORTEXPORT_FETCH_ONE_SUCCESS, importExportId: action.importExportId, importExport});
	}
	catch(err) {
		yield put({type: Actions.IMPORTEXPORT_FETCH_ONE_FAILURE, importExportId: action.importExportId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ImportExportAction} action
 */
export function* saveImportExport(action: ImportExportAction) {
    // console.debug('⮂ IMPORTEXPORT_SAVE saga', action.importExport?.id);
    
	if (action.importExport === undefined || action.importExport === null) {
		console.error('saveImportExport received a null');
		throw new Error('saveImportExport received a null');
	}

	if (!(action.importExport instanceof ImportExport)) {
		action.importExport = new ImportExport(action.importExport);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'importExport', action.importExport);
		if (addResult.isErr()) {
			console.error(`⮂ saveImportExport: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.IMPORTEXPORT_SAVE_FAILURE, importExport: action.importExport, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IMPORTEXPORT_SAVE, importExport: action.importExport});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'importExport', action.importExport.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.IMPORTEXPORT_SAVE_FAILURE, importExport: action.importExport, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {ImportExportAction} action
 * @param {ImportExport[]} action.importExports
 */
export function* saveManyImportExports(action: ImportExportAction) {
    // console.debug('⮂ IMPORTEXPORT_SAVE_MANY saga', action.importExports.map(importExport => importExport.id).join(','));
    
    if (!Array.isArray(action.importExports)) {
        console.error('saveManyImportExports expected an array, received', action.importExports);
        throw new Error('saveManyImportExports expected an array');
    }

	try {
		const toSave: ImportExport[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.importExports.length; i++) {
			const importExport = action.importExports[i];
			importExport.persisted = false;
			toSave.push(importExport);
		}

		const addResult = yield call(idb.add, 'importExport', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyImportExports: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.IMPORTEXPORT_SAVE_MANY_FAILURE, importExports: action.importExports, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IMPORTEXPORT_SAVE_MANY, importExports: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.IMPORTEXPORT_SAVE_MANY_FAILURE, importExports: action.importExports, status: 500, err});

		console.error(`Error saving ${action.importExports.length} importExports to database`, err);
	}
}

/**
 * @param {ImportExportAction} action
 */
export function* removeImportExport(action: ImportExportAction) {
    const userId = getCurrentUserId();
	if (!userId || action.importExport === undefined || action.importExport === null || !(action.importExport instanceof ImportExport)) {
			console.error('removeImportExport expected type ImportExport, received', action.importExport);
			throw new Error('removeImportExport expected type ImportExport');
		}

	try {
		// Mark as deleted
		action.importExport.status = 'D';
		action.importExport.addedOn = newHRTimestamp();
		action.importExport.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'importExport', action.importExport);
		if (addResult.isErr()) {
			console.error(`⮂ removeImportExport: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.IMPORTEXPORT_REMOVE_FAILURE, importExport: action.importExport, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IMPORTEXPORT_REMOVE, importExport: action.importExport});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.IMPORTEXPORT_REMOVE_FAILURE, importExport: action.importExport, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this ImportExport.
	*
	* @param {ImportExportAction} action
	*/
export function* restoreImportExport(action: ImportExportAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreImportExport expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.importExport === undefined || action.importExport === null) {
        console.error('restoreImportExport expected a ImportExport, received null');
        throw new Error('restoreImportExport expected a ImportExport, received null');
    }
    else if (!(action.importExport instanceof ImportExport)) action.importExport = new ImportExport(action.importExport); 
    
	try {
		// Mark as restored
		action.importExport.status = 'U';
		action.importExport.addedOn = newHRTimestamp();
		action.importExport.addedBy = userId;
		action.importExport.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'importExport', action.importExport);
		if (addResult.isErr()) {
			console.error(`⮂ restoreImportExport: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.IMPORTEXPORT_RESTORE_FAILURE, importExport: action.importExport, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IMPORTEXPORT_RESTORE, importExport: action.importExport});
	}
	catch(err) {
		yield put({type: Actions.IMPORTEXPORT_RESTORE_FAILURE, importExport: action.importExport, status: 500, err});

		console.error('Error restoring from database', err);
	}
}